import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import LoadingButton from "../../../components/ui/LoadingButton";
import IconButton from "../../../components/ui/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useIntl } from "../../../contexts/IntlContext";
import api from "../../../services/api";
import ConfirmDialog from "../../../components/ConfirmDialog";
import StandardTablePagination from "../../../components/table/StandardTablePagination";
import { ACCEPTED_IMAGE_TYPES_STRING } from "../../../constants/imageUpload";
import { getCardSurfaceSx } from "../../../themes/darkSurfaceColors";

const ROWS_PER_PAGE_OPTIONS = [12, 24, 48, 96];

type MediaRow = { id: number; name: string; dataUrl?: string | null };

const isDuplicateNameError = (message: string) => {
  const lower = message.toLowerCase();
  return lower.includes("uk_name") || lower.includes("constraint") || lower.includes("duplicate");
};

const makeUniqueFilename = (originalName: string) => {
  const trimmed = (originalName || "image").trim() || "image";
  const dotIndex = trimmed.lastIndexOf(".");
  const hasExt = dotIndex > 0 && dotIndex < trimmed.length - 1;
  const base = hasExt ? trimmed.slice(0, dotIndex) : trimmed;
  const ext = hasExt ? trimmed.slice(dotIndex) : "";
  const suffix = `_${Date.now()}`;
  return `${base}${suffix}${ext}`;
};

export default function MediaManager() {
  const intl = useIntl();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [rows, setRows] = useState<MediaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MediaRow | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(24);
  const [count, setCount] = useState(0);

  const cardSx = useMemo(() => getCardSurfaceSx(theme, isDark), [theme, isDark]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ data: MediaRow[]; count: number }>("/app/media", {
        params: { limit: rowsPerPage, offset: page * rowsPerPage, includeData: true },
      });
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      data.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      setRows(data);
      setCount(response.data?.count ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const onPickFile = () => inputRef.current?.click();

  const onFileSelected = async (file: File | null) => {
    if (!file) return;
    try {
      const dataBase64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });

      const attemptCreate = async (name: string) => await api.post("/app/media", { name, dataBase64 });
      try {
        await attemptCreate(file.name);
      } catch (e: any) {
        const msg = typeof e?.response?.data?.message === "string" ? e.response.data.message : String(e?.message ?? "");
        if (!isDuplicateNameError(msg)) throw e;
        await attemptCreate(makeUniqueFilename(file.name));
      }

      toast.success(intl.formatMessage({ id: "global.saved" }));
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (e: any) {
      const msg = typeof e?.response?.data?.message === "string" ? e.response.data.message : String(e?.message ?? "");
      toast.error(msg || intl.formatMessage({ id: "global.generic_error" }));
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const del = async (id: number) => {
    await api.delete(`/app/media/${id}`);
    if (rows.length === 1 && page > 0) {
      setPage(page - 1);
      return;
    }
    await load();
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await del(target.id);
      toast.success(intl.formatMessage({ id: "global.saved" }));
    } catch (e: any) {
      const msg =
        typeof e?.response?.data?.message === "string"
          ? e.response.data.message
          : String(e?.message ?? "");
      toast.error(msg || intl.formatMessage({ id: "global.generic_error" }));
    }
  };

  return (
    <Card sx={cardSx}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h6">{intl.formatMessage({ id: "liveMedia.title" })}</Typography>
          <Box>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES_STRING}
              style={{ display: "none" }}
              onChange={(e) => onFileSelected(e.target.files?.[0] ?? null)}
            />
            <LoadingButton
              startIcon={<UploadFileIcon />}
              variant="contained"
              onClick={onPickFile}
              disabled={loading}
            >
              {intl.formatMessage({ id: "liveMedia.upload" })}
            </LoadingButton>
          </Box>
        </Stack>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block", mb: 2 }}
        >
          {intl.formatMessage({
            id: "upload.acceptedFormats",
            defaultMessage: "Formatos aceitos: PNG, JPEG, GIF",
          })}
        </Typography>

        {rows.length ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 2,
            }}
          >
            {rows.map((r) => (
              <Box
                key={r.id}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "action.hover",
                  }}
                >
                  {r.dataUrl ? (
                    <Box
                      component="img"
                      src={r.dataUrl}
                      alt={r.name}
                      title={r.name}
                      sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <ImageNotSupportedIcon sx={{ color: "text.disabled" }} />
                  )}
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={0.5}
                  sx={{ px: 1, py: 0.5 }}
                >
                  <Typography variant="body2" noWrap title={r.name} sx={{ minWidth: 0 }}>
                    {r.name}
                  </Typography>
                  <IconButton onClick={() => setDeleteTarget(r)} aria-label="delete">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            {intl.formatMessage({ id: "liveMedia.empty" })}
          </Typography>
        )}

        {rows.length > 0 && (
          <StandardTablePagination
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        )}
      </CardContent>

      <ConfirmDialog
        open={!!deleteTarget}
        title={intl.formatMessage({ id: "common.confirm" })}
        message={intl.formatMessage({ id: "deleteDialog.confirmMessage" })}
        confirmLabel={intl.formatMessage({ id: "common.yes" })}
        cancelLabel={intl.formatMessage({ id: "common.no" })}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Card>
  );
}
